import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export interface FinancialOverview {
  escrowBalance: string | null;
  escrowError: boolean;
  totalVirtualBalance: string;
  netPosition: string | null;
  totalFintechRevenue: string;
  totalProcessorCost: string;
  totalMembershipRevenue: string;
  totalGrossReceived: string;
  totalWithdrawn: string;
  totalCashInFees: string;
  totalMembershipFees: string;
  totalFeesCollected: string;
  membershipFeeBreakdown: {
    totalCharged: string;
    totalPending: string;
    chargedCount: number;
    pendingCount: number;
    exemptCount: number;
    exemptTypes: string[];
  };
  chargedAccountsCount: number;
  pendingAccountsCount: number;
  exemptAccountsCount: number;
}

export interface AdminTransaction {
  id: string;
  type: string;
  grossAmount: string;
  totalFees: string;
  netAmount: string;
  accountHolderName: string;
  accountHolderDocument: string;
  virtualAccountId: string;
  createdAt: string;
  status: string;
  fintechFee: string;
  processorFee: string;
  description: string | null;
}

export interface AccountSummary {
  id: string;
  holderName: string;
  holderDocument: string;
  accountType: string;
  availableBalance: string;
  blockedBalance: string;
  totalBalance: string;
  totalReceived: string;
  totalSent: string;
  totalCashInFeesPaid: string;
  totalMembershipFeesPaid: string;
  membershipFeeStatus: string;
  lastMovementAt: string | null;
  hasActivity: boolean;
  isZeroBalance: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OverviewFilters {
  startDate?: string;
  endDate?: string;
}

export interface TransactionFilters {
  type?: string;
  startDate?: string;
  endDate?: string;
  virtualAccountId?: string;
  page?: number;
  limit?: number;
}

export interface AccountFilters {
  search?: string;
  accountType?: string;
  feeStatus?: string;
  balanceStatus?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
}

export function useFinancialOverview(filters?: OverviewFilters) {
  return useQuery<FinancialOverview>({
    queryKey: ['financial-overview', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      const qs = params.toString();
      const { data } = await api.get(`/admin/financial-overview${qs ? '?' + qs : ''}`);
      return data;
    },
  });
}

export function useFinancialTransactions(filters: TransactionFilters) {
  return useQuery<PaginatedResponse<AdminTransaction>>({
    queryKey: ['financial-transactions', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.type) params.set('type', filters.type);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.virtualAccountId) params.set('virtualAccountId', filters.virtualAccountId);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const { data } = await api.get(`/admin/financial-overview/transactions${qs ? '?' + qs : ''}`);
      return data;
    },
  });
}

export function useFinancialAccounts(filters: AccountFilters) {
  return useQuery<PaginatedResponse<AccountSummary>>({
    queryKey: ['financial-accounts', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.accountType) params.set('accountType', filters.accountType);
      if (filters.feeStatus) params.set('feeStatus', filters.feeStatus);
      if (filters.balanceStatus) params.set('balanceStatus', filters.balanceStatus);
      if (filters.sortBy) params.set('sortBy', filters.sortBy);
      if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const { data } = await api.get(`/admin/financial-overview/accounts${qs ? '?' + qs : ''}`);
      return data;
    },
  });
}
